import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import {
    clearToken, getToken,
    adminListMessages, adminMarkRead, adminDeleteMessage,
    adminListVolunteers, adminUpdateVolunteer, adminDeleteVolunteer,
    adminDashboard, adminMe,
} from "../lib/api";
import { AdminLogin } from "../components/admin/AdminLogin";
import { AdminStats } from "../components/admin/AdminStats";
import { MessagesTab } from "../components/admin/MessagesTab";
import { VolunteersTab } from "../components/admin/VolunteersTab";

export default function Admin() {
    const [authed, setAuthed] = useState(false);
    const [tab, setTab] = useState("messages");
    const [dash, setDash] = useState(null);
    const [messages, setMessages] = useState([]);
    const [volunteers, setVolunteers] = useState([]);

    const refresh = useCallback(async () => {
        try {
            const [d, m, v] = await Promise.all([
                adminDashboard(),
                adminListMessages(),
                adminListVolunteers(),
            ]);
            setDash(d);
            setMessages(m);
            setVolunteers(v);
        } catch (e) {
            if (e.response?.status === 401) {
                clearToken();
                setAuthed(false);
            }
        }
    }, []);

    // Bootstrap: if a token already exists, validate it.
    useEffect(() => {
        if (!getToken()) return;
        adminMe()
            .then(() => setAuthed(true))
            .catch(() => clearToken());
    }, []);

    // When authed flips true, load data.
    useEffect(() => {
        if (authed) refresh();
    }, [authed, refresh]);

    const handleLogout = () => {
        clearToken();
        setAuthed(false);
        toast.success("Sesión cerrada");
    };

    const handleMarkRead = (id) => adminMarkRead(id).then(refresh);
    const handleDeleteMessage = (id) => {
        if (window.confirm("¿Eliminar mensaje?")) adminDeleteMessage(id).then(refresh);
    };
    const handleStatusChange = (id, estado) => adminUpdateVolunteer(id, estado).then(refresh);
    const handleDeleteVolunteer = (id) => {
        if (window.confirm("¿Eliminar voluntario?")) adminDeleteVolunteer(id).then(refresh);
    };

    if (!authed) return <AdminLogin onAuthenticated={() => setAuthed(true)} />;

    const TABS = [
        { k: "messages", label: `Mensajes (${messages.length})` },
        { k: "volunteers", label: `Voluntarios (${volunteers.length})` },
    ];

    return (
        <section className="py-12 bg-brand-cream min-h-[80vh]" data-testid="admin-dashboard">
            <div className="container-fe">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-display font-bold text-3xl lg:text-4xl text-brand-navy">Panel administrativo</h1>
                        <p className="text-muted-foreground mt-1">Mensajes y voluntarios en un solo lugar.</p>
                    </div>
                    <button onClick={handleLogout} className="btn-secondary-pill" data-testid="admin-logout">
                        <LogOut size={16} /> Cerrar sesión
                    </button>
                </div>

                <AdminStats dash={dash} />

                <div className="flex gap-2 mb-6 border-b border-black/10">
                    {TABS.map((t) => (
                        <button
                            key={t.k}
                            type="button"
                            onClick={() => setTab(t.k)}
                            data-testid={`admin-tab-${t.k}`}
                            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                                tab === t.k
                                    ? "border-brand-green text-brand-green"
                                    : "border-transparent text-muted-foreground hover:text-brand-navy"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "messages" && (
                    <MessagesTab messages={messages} onMarkRead={handleMarkRead} onDelete={handleDeleteMessage} />
                )}
                {tab === "volunteers" && (
                    <VolunteersTab volunteers={volunteers} onStatusChange={handleStatusChange} onDelete={handleDeleteVolunteer} />
                )}
            </div>
        </section>
    );
}
